import courseCompletionMitraReportAPI from '@/api/course-completion-mitra-report/course-completion-mitra-report.js';
import { normalizeBatchList } from '@/utils/batchIdentity';

const state = () => ({
  courses: [],
  courseDetail: null,
  summary: {
    this_year: {
      submitted: 0,
      rejected: 0,
      approved: 0,
      completed: 0,
      percentage: 0,
      total: 0
    },
    history: {
      submitted: 0,
      rejected: 0,
      approved: 0,
      completed: 0,
      percentage: 0,
      total: 0
    }
  },
  pagination: {
    total: 0,
    page: 1,
    limit: 12,
    totalPage: 0
  },
  loading: false,
  summaryLoading: false,
  error: null
});

const mutations = {
  SET_COURSES(state, courses) {
    state.courses = courses;
  },
  SET_COURSE_DETAIL(state, courseDetail) {
    state.courseDetail = courseDetail;
  },
  SET_SUMMARY(state, summary) {
    state.summary = summary;
  },
  SET_PAGINATION(state, pagination) {
    state.pagination = {
      total: pagination.total || 0,
      page: pagination.page || 1,
      limit: pagination.limit || 12,
      totalPage: pagination.totalPage || 0
    };
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_SUMMARY_LOADING(state, loading) {
    state.summaryLoading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  UPDATE_COURSE_STATUS(state, { courseId, status, certificate_path }) {
    const course = state.courses.find(c => c.id === courseId);
    if (course) {
      course.status = status;
      if (certificate_path) {
        course.certificate_path = certificate_path;
      }
    }
  }
};

const getters = {
  courses: (state) => state.courses,
  courseDetail: (state) => state.courseDetail,
  summary: (state) => state.summary,
  pagination: (state) => state.pagination,
  loading: (state) => state.loading,
  summaryLoading: (state) => state.summaryLoading,
  error: (state) => state.error
};

const actions = {
  async getCourses({ commit }, params = {}) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    
    try {
      const response = await courseCompletionMitraReportAPI.getCourses(params);
      const data = response.data.data;
      
      commit('SET_COURSES', data.data || []);
      commit('SET_PAGINATION', {
        total: data.total || 0,
        page: data.page || 1,
        limit: data.limit || 12,
        totalPage: data.totalPage || 0
      });
      
      return data;
    } catch (error) {
      console.error('Error fetching courses:', error);
      commit('SET_ERROR', error.message || 'Failed to fetch courses');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async getCourseDetail({ commit, state }, payload) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    
    try {
      const courseId = typeof payload === 'object' ? payload.courseId : payload;
      const params = typeof payload === 'object' ? payload.params : undefined;
      // Fetch both batches and course catalog data in parallel
      const [batchesResponse, catalogResponse] = await Promise.all([
        courseCompletionMitraReportAPI.getCourseDetail(courseId, params),
        courseCompletionMitraReportAPI.getCourseDetailFromCatalog(courseId)
      ]);

      const batches = normalizeBatchList(batchesResponse.data.data);
      const catalogData = catalogResponse.data.data || {};
      
      const course = state.courses.find(c => c.id === courseId);
      
      // Merge enrollment data + catalog data + batches
      const courseDetailWithBatches = {
        ...(course || {}),
        ...catalogData, // Include all catalog data (platform, provider, competencies, etc.)
        batches: batches
      };
      
      commit('SET_COURSE_DETAIL', courseDetailWithBatches);
      return courseDetailWithBatches;
    } catch (error) {
      console.error('Error fetching course detail:', error);
      commit('SET_ERROR', error.message || 'Failed to fetch course detail');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async getSummary({ commit }, params = {}) {
    commit('SET_SUMMARY_LOADING', true);
    
    try {
      const response = await courseCompletionMitraReportAPI.getSummary(params);
      const summaryData = response.data.data || response.data;
      commit('SET_SUMMARY', summaryData);
      return summaryData;
    } catch (error) {
      console.error('Error fetching summary:', error);
      throw error;
    } finally {
      commit('SET_SUMMARY_LOADING', false);
    }
  },

  async uploadCertificate({ commit }, { courseId, formData }) {
    commit('SET_LOADING', true);
    
    try {
      const response = await courseCompletionMitraReportAPI.uploadCertificate(courseId, formData);
      
      commit('UPDATE_COURSE_STATUS', {
        courseId,
        status: 'in_review'
      });
      
      return response.data;
    } catch (error) {
      console.error('Error uploading certificate:', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async submitCompletion({ commit }, { enrollmentId, formData }) {
    commit('SET_LOADING', true);
    
    try {
      const response = await courseCompletionMitraReportAPI.submitCompletion(enrollmentId, formData);
      return response.data;
    } catch (error) {
      console.error('Error submitting completion:', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async downloadCertificate({ commit }, courseId) {
    try {
      const response = await courseCompletionMitraReportAPI.downloadCertificate(courseId);
      return response.data;
    } catch (error) {
      console.error('Error downloading certificate:', error);
      throw error;
    }
  },

  async submitCompletionReview({ commit }, { completionId, data }) {
    commit('SET_LOADING', true);
    
    try {
      const response = await courseCompletionMitraReportAPI.submitCompletionReview(completionId, data);
      return response.data;
    } catch (error) {
      console.error('Error submitting completion review:', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  clearCourseDetail({ commit }) {
    commit('SET_COURSE_DETAIL', null);
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
};
